import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { firestore } from "@redux/firebase";

/**
 * Verifica che il testo cached sia ragionevole rispetto alla dimensione del file.
 * Un PDF di grandi dimensioni non può avere solo pochi caratteri.
 *
 * @param {string} text - Testo cached
 * @param {number} fileSize - Dimensione del file in bytes
 * @param {string} fileType - Tipo MIME del file
 * @returns {boolean} - true se il testo sembra valido
 */
const isCachedTextValid = (text, fileSize, fileType) => {
  if (!text || text.length === 0) return false;

  // Per PDF: il testo deve essere almeno 0.1% della dimensione del file
  // Es: un PDF di 1MB dovrebbe avere almeno ~1000 caratteri
  if (fileType === 'application/pdf' && fileSize) {
    const minExpectedChars = Math.max(100, Math.floor(fileSize * 0.001));
    if (text.length < minExpectedChars) {
      console.log(`Cache SKIP: testo troppo corto (${text.length} chars) per file di ${fileSize} bytes (minimo ${minExpectedChars})`);
      return false;
    }
  }

  // Soglia minima assoluta: almeno 100 caratteri per qualsiasi documento
  if (text.length < 100) {
    console.log(`Cache SKIP: testo troppo corto (${text.length} chars < 100 minimo)`);
    return false;
  }

  return true;
};

/**
 * Cerca un documento già esistente nella cache basandosi su identificatori univoci.
 * Permette di evitare ri-estrazione del testo per documenti già processati.
 *
 * @param {string} userID - ID utente Firebase
 * @param {string} fileName - Nome del file (con estensione)
 * @param {string} fileType - Tipo MIME del file
 * @param {number} fileSize - Dimensione del file in bytes
 * @returns {Promise<{text: string, documentId: string} | null>} - Documento cachato o null
 */
export const findCachedDocument = async (userID, fileName, fileType, fileSize) => {
  if (!userID || !fileName) {
    return null;
  }

  try {
    // Estrai il titolo dal nome file (senza estensione)
    const title = fileName.includes('.')
      ? fileName.substring(0, fileName.lastIndexOf('.'))
      : fileName;

    console.log(`Cache lookup: "${title}" (${fileType}, ${fileSize} bytes)`);

    // Determina i tipi da cercare
    // Per le immagini, cerchiamo sia il tipo originale che "image/multiple"
    const typesToSearch = [fileType];
    if (fileType?.startsWith('image/') && fileType !== 'image/multiple') {
      typesToSearch.push('image/multiple');
    }

    // Prova con ogni tipo
    for (const searchType of typesToSearch) {
      // Cerca con fileSize esatto (match preciso)
      if (fileSize) {
        const qWithSize = query(
          collection(firestore, "documents"),
          where("uploadedBy", "==", userID),
          where("title", "==", title),
          where("type", "==", searchType),
          where("fileSize", "==", fileSize),
          limit(1)
        );

        const snapshotWithSize = await getDocs(qWithSize);

        if (!snapshotWithSize.empty) {
          const doc = snapshotWithSize.docs[0];
          const data = doc.data();

          if (isCachedTextValid(data.text, fileSize, fileType)) {
            console.log(`Cache HIT (con fileSize): "${title}" - ${data.text.length} caratteri`);
            return {
              text: data.text,
              documentId: doc.id,
              title: data.title
            };
          }
        }
      }

      // Fallback: cerca senza fileSize (per documenti vecchi)
      const qWithoutSize = query(
        collection(firestore, "documents"),
        where("uploadedBy", "==", userID),
        where("title", "==", title),
        where("type", "==", searchType),
        limit(1)
      );

      const snapshotWithoutSize = await getDocs(qWithoutSize);

      if (!snapshotWithoutSize.empty) {
        const doc = snapshotWithoutSize.docs[0];
        const data = doc.data();

        if (isCachedTextValid(data.text, fileSize, fileType)) {
          console.log(`Cache HIT (senza fileSize): "${title}" - ${data.text.length} caratteri`);
          return {
            text: data.text,
            documentId: doc.id,
            title: data.title
          };
        }
      }
    }

    console.log("Cache MISS: documento non trovato");
    return null;
  } catch (error) {
    // Non bloccare il flusso in caso di errore cache
    console.warn("Errore cache lookup (continuo con estrazione normale):", error.message);
    return null;
  }
};

/**
 * Processa un file verificando prima la cache.
 * Se il documento è già in cache, restituisce il testo cached.
 * Altrimenti procede con l'estrazione normale.
 * 
 * @param {File} file - File da processare
 * @param {string} userID - ID utente Firebase
 * @param {Function} extractionFn - Funzione di estrazione da usare se cache miss
 * @returns {Promise<Object>} - Oggetto con testo estratto e metadata
 */
export const processFileWithCache = async (file, userID, extractionFn) => {
  // Verifica cache
  const cached = await findCachedDocument(
    userID,
    file.name,
    file.type,
    file.size
  );

  if (cached) {
    return {
      name: file.name,
      type: file.type,
      size: file.size,
      extractedText: cached.text,
      fromCache: true,
      cachedDocumentId: cached.documentId
    };
  }

  // Cache miss: procedi con estrazione normale
  const result = await extractionFn(file);
  
  return {
    ...result,
    size: file.size,
    fromCache: false
  };
};

export default findCachedDocument;
